import { useState } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

const Account = ({ onBack, onUpgrade, onDeleteAllConversations }) => {
  const { user, profile, signOut } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  if (!user) return null;

  const avatarUrl = user.user_metadata?.avatar_url;
  const fullName = user.user_metadata?.full_name || "User";
  const email = user.email;
  const tier = profile?.tier || "free";
  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "";

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    setPortalError("");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch("/api/create-portal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ userEmail: email }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setPortalError(data.error || "Something went wrong. Please try again.");
        setPortalLoading(false);
        return;
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setPortalError("Something went wrong. Please try again.");
        setPortalLoading(false);
      }
    } catch {
      setPortalError("Something went wrong. Please try again.");
      setPortalLoading(false);
    }
  };

  return (
    <section
      style={{
        padding: "clamp(64px, 10vh, 120px) 24px",
        maxWidth: "600px", margin: "0 auto",
      }}
      aria-labelledby="account-heading"
    >
      <button
        onClick={onBack}
        aria-label="Go back to home page"
        style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: "14px", color: "var(--ink-muted)", fontFamily: "inherit",
          marginBottom: "32px", padding: 0,
        }}
        onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
        onMouseLeave={(e) => e.target.style.color = "var(--ink-muted)"}
      >
        &larr; Back
      </button>

      <h1 id="account-heading" style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: "28px", fontWeight: "400",
        color: "var(--ink)", letterSpacing: "-0.02em",
        marginBottom: "24px",
      }}>
        Your Account
      </h1>

      <div style={{
        backgroundColor: "#fff", borderRadius: "12px",
        border: "1px solid var(--border)", padding: "32px",
      }}>
        {/* Profile header */}
        <div style={{
          display: "flex", alignItems: "center", gap: "16px",
          marginBottom: "28px", paddingBottom: "24px",
          borderBottom: "1px solid var(--border-light)",
        }}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={`${fullName}'s profile photo`}
              style={{ width: "48px", height: "48px", borderRadius: "50%" }}
              referrerPolicy="no-referrer"
            />
          ) : (
            <div
              style={{
                width: "48px", height: "48px", borderRadius: "50%",
                backgroundColor: "var(--accent)", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "18px", fontWeight: "600",
              }}
              aria-hidden="true"
            >
              {fullName.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <p style={{ fontSize: "17px", fontWeight: "600", color: "var(--ink)", marginBottom: "2px" }}>
              {fullName}
            </p>
            <p style={{ fontSize: "14px", color: "var(--ink-muted)" }}>
              {email}
            </p>
          </div>
        </div>

        {/* Plan info */}
        <div style={{ marginBottom: "24px" }}>
          <p style={{ fontSize: "13px", color: "var(--ink-muted)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Your plan
          </p>
          <div style={{
            display: "flex", alignItems: "center", gap: "10px",
          }}>
            <span style={{
              display: "inline-block", padding: "4px 12px",
              borderRadius: "20px", fontSize: "13px", fontWeight: "600",
              backgroundColor: tier === "paid" ? "var(--accent)" : "var(--surface-warm)",
              color: tier === "paid" ? "#fff" : "var(--ink-soft)",
              border: tier === "paid" ? "none" : "1px solid var(--border)",
            }}>
              {tier === "paid" ? "Pro" : "Free"}
            </span>
            {tier === "free" && onUpgrade && (
              <button
                onClick={onUpgrade}
                style={{
                  fontSize: "13px", color: "var(--accent)", fontWeight: "500",
                  background: "none", border: "none", cursor: "pointer",
                  fontFamily: "inherit", textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
                onMouseEnter={(e) => e.target.style.color = "var(--accent-light)"}
                onMouseLeave={(e) => e.target.style.color = "var(--accent)"}
              >
                Upgrade to Pro
              </button>
            )}
          </div>
        </div>

        {/* Manage subscription (Pro users) */}
        {tier === "paid" && (
          <div style={{ marginBottom: "24px" }}>
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              style={{
                fontSize: "14px", fontWeight: "500",
                color: "var(--accent)",
                background: "none", border: "none", cursor: portalLoading ? "default" : "pointer",
                fontFamily: "inherit", padding: 0,
                textDecoration: "underline", textUnderlineOffset: "2px",
                opacity: portalLoading ? 0.6 : 1,
              }}
            >
              {portalLoading ? "Opening..." : "Manage subscription"}
            </button>
            {portalError && (
              <p style={{ fontSize: "13px", color: "#c44", marginTop: "8px" }} role="alert">
                {portalError}
              </p>
            )}
          </div>
        )}

        {/* Member since */}
        {memberSince && (
          <div style={{ marginBottom: "28px" }}>
            <p style={{ fontSize: "13px", color: "var(--ink-muted)", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Member since
            </p>
            <p style={{ fontSize: "15px", color: "var(--ink-soft)" }}>
              {memberSince}
            </p>
          </div>
        )}

        {/* Data management — shown to every signed-in user so people who cancel
            can still delete history saved while they were on Pro */}
        {(

          <div style={{
            marginBottom: "28px", paddingTop: "24px",
            borderTop: "1px solid var(--border-light)",
          }}>
            <p style={{
              fontSize: "13px", color: "var(--ink-muted)", marginBottom: "12px",
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}>
              Your data
            </p>

            {deleteSuccess ? (
              <p style={{
                fontSize: "14px", color: "var(--accent)", fontWeight: "500",
              }} role="status">
                All conversations deleted.
              </p>
            ) : deleteConfirm ? (
              <div>
                <p style={{
                  fontSize: "14px", color: "var(--ink-soft)", marginBottom: "12px",
                  lineHeight: 1.6,
                }}>
                  This will permanently delete all your saved conversations.
                  This action cannot be undone.
                </p>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={async () => {
                      setDeleteLoading(true);
                      setDeleteError("");
                      const ok = onDeleteAllConversations
                        ? await onDeleteAllConversations()
                        : true;
                      setDeleteLoading(false);
                      setDeleteConfirm(false);
                      if (ok) {
                        setDeleteSuccess(true);
                      } else {
                        setDeleteError("Something went wrong. Please try again.");
                      }
                    }}
                    disabled={deleteLoading}
                    style={{
                      padding: "8px 16px", fontSize: "13px", fontWeight: "500",
                      backgroundColor: "#c44", color: "#fff",
                      border: "none", borderRadius: "6px",
                      cursor: deleteLoading ? "default" : "pointer",
                      fontFamily: "inherit",
                      opacity: deleteLoading ? 0.6 : 1,
                    }}
                  >
                    {deleteLoading ? "Deleting..." : "Yes, delete everything"}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    style={{
                      padding: "8px 16px", fontSize: "13px", fontWeight: "500",
                      backgroundColor: "var(--surface-warm)", color: "var(--ink-soft)",
                      border: "1px solid var(--border)", borderRadius: "6px",
                      cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={() => { setDeleteConfirm(true); setDeleteError(""); }}
                  style={{
                    fontSize: "14px", fontWeight: "500",
                    color: "#c44",
                    background: "none", border: "none",
                    cursor: "pointer", fontFamily: "inherit", padding: 0,
                    textDecoration: "underline", textUnderlineOffset: "2px",
                  }}
                >
                  Delete all conversations
                </button>
                {deleteError && (
                  <p style={{ fontSize: "13px", color: "#c44", marginTop: "8px" }} role="alert">
                    {deleteError}
                  </p>
                )}
              </>
            )}

            <p style={{ fontSize: "13px", color: "var(--ink-muted)", marginTop: "16px", lineHeight: 1.6 }}>
              {tier === "paid"
                ? "If you cancel your subscription, your conversation history will be deleted within 30 days."
                : "Any history saved while you were on Pro is deleted within 30 days of cancellation, or right away if you use the button above."}
            </p>
          </div>
        )}

        {/* Sign out */}
        <button
          onClick={signOut}
          style={{
            padding: "12px 24px", fontSize: "14px", fontWeight: "500",
            backgroundColor: "var(--surface-warm)", color: "var(--ink-soft)",
            border: "1px solid var(--border)", borderRadius: "6px",
            cursor: "pointer", fontFamily: "inherit",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => { e.target.style.backgroundColor = "var(--surface-mid)"; }}
          onMouseLeave={(e) => { e.target.style.backgroundColor = "var(--surface-warm)"; }}
        >
          Sign out
        </button>
      </div>
    </section>
  );
};

export default Account;
